import React, { useState, useEffect, useRef } from'react';
import { useParams, useNavigate, useLocation } from'react-router-dom';
import { Music2, ArrowLeft, Plus, X, Loader2, Save, Globe, Instagram, Facebook, Youtube, Twitter, Upload, Image as ImageIcon } from'lucide-react';
import { Button } from'../components/ui/button';
import { Input } from'../components/ui/input';
import { Label } from'../components/ui/label';
import { Textarea } from'../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from'../components/ui/card';
import { Badge } from'../components/ui/badge';
import { toast } from'sonner';
import { API_URL } from'../utils/api';

const PERFORMANCE_TYPE_OPTIONS = [
'Singer','Band','Live Music','Solo Artist','Duo', 
'Country Artist','Line Dance Performer','Entertainer','Other'
];

const GENRE_OPTIONS = [
'Country','Pop','Rock','Dance','Folk','Americana', 
'Western Swing','Bluegrass','Contemporary','Traditional','Other'
];

export default function AddEditArtistPage() {
 const { artistId } = useParams();
 const navigate = useNavigate();
 const location = useLocation();
 const isEditing = !!artistId;
 const isAdminRoute = location.pathname.startsWith('/admin');

 const [loading, setLoading] = useState(isEditing);
 const [saving, setSaving] = useState(false);
 const [formData, setFormData] = useState({
 name:'',
 stage_name:'',
 bio:'',
 performance_types: [],
 genres: [],
 location:'',
 phone:'',
 email:'',
 website:'',
 social_links: {},
 image_url:'',
 video_urls: [],
 music_urls: []
 });

 const [newVideoUrl, setNewVideoUrl] = useState('');
 const [newMusicUrl, setNewMusicUrl] = useState('');
 const [newSocialPlatform, setNewSocialPlatform] = useState('');
 const [newSocialUrl, setNewSocialUrl] = useState('');
 const [uploadingImage, setUploadingImage] = useState(false);
 const imageInputRef = useRef(null);

 useEffect(() => {
 if (isEditing) {
 fetchArtist();
 }
 }, [artistId]);

 const fetchArtist = async () => {
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/artists/${artistId}`, {
 headers: token ? {'Authorization': `Bearer ${token}` } : {}
 });
 if (response.ok) {
 const data = await response.json();
 setFormData({
 name: data.name ||'',
 stage_name: data.stage_name ||'',
 bio: data.bio ||'',
 performance_types: data.performance_types || [],
 genres: data.genres || [],
 location: data.location ||'',
 phone: data.phone ||'',
 email: data.email ||'',
 website: data.website ||'',
 social_links: data.social_links || {},
 image_url: data.image_url ||'',
 video_urls: data.video_urls || [],
 music_urls: data.music_urls || []
 });
 } else {
 toast.error('Failed to load artist');
 navigate('/dashboard');
 }
 } catch (error) {
 toast.error('Failed to load artist');
 navigate(isAdminRoute ? '/admin' : '/dashboard');
 } finally {
 setLoading(false);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 
 if (!formData.name.trim()) {
 toast.error('Name is required');
 return;
 }
 if (!formData.bio.trim()) {
 toast.error('Bio is required');
 return;
 }
 if (!formData.location.trim()) {
 toast.error('Location is required');
 return;
 }

 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const url = isEditing 
 ? `${API_URL}/api/artists/${artistId}`
 : `${API_URL}/api/artists`;
 
 // If admin is creating, include auto_approve flag
 const submitData = isAdminRoute && !isEditing 
   ? { ...formData, auto_approve: true }
   : formData;
 
 const response = await fetch(url, {
 method: isEditing ?'PUT' :'POST',
 headers: {
'Content-Type':'application/json',
'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify(submitData)
 });

 if (response.ok) {
 const data = await response.json();
 if (isAdminRoute) {
   toast.success(isEditing ? 'Artist profile updated!' : 'Artist profile created and approved!');
   navigate('/admin');
 } else {
   toast.success(isEditing ? 'Artist profile updated!' : 'Artist profile created! It will be visible once approved.');
   navigate('/dashboard');
 }
 } else {
 const error = await response.json();
 toast.error(error.detail ||'Failed to save artist profile');
 }
 } catch (error) {
 toast.error('Failed to save artist profile');
 } finally {
 setSaving(false);
 }
 };

 const togglePerformanceType = (type) => {
 setFormData(prev => ({
 ...prev,
 performance_types: prev.performance_types.includes(type)
 ? prev.performance_types.filter(t => t !== type)
 : [...prev.performance_types, type]
 }));
 };

 const toggleGenre = (genre) => {
 setFormData(prev => ({
 ...prev,
 genres: prev.genres.includes(genre)
 ? prev.genres.filter(g => g !== genre)
 : [...prev.genres, genre]
 }));
 };

 const addVideoUrl = () => {
 if (newVideoUrl.trim() && !formData.video_urls.includes(newVideoUrl.trim())) {
 setFormData(prev => ({
 ...prev,
 video_urls: [...prev.video_urls, newVideoUrl.trim()]
 }));
 setNewVideoUrl('');
 }
 };

 const removeVideoUrl = (index) => {
 setFormData(prev => ({
 ...prev,
 video_urls: prev.video_urls.filter((_, i) => i !== index)
 }));
 };

 const addMusicUrl = () => {
 if (newMusicUrl.trim() && !formData.music_urls.includes(newMusicUrl.trim())) {
 setFormData(prev => ({
 ...prev,
 music_urls: [...prev.music_urls, newMusicUrl.trim()]
 }));
 setNewMusicUrl('');
 }
 };

 const removeMusicUrl = (index) => {
 setFormData(prev => ({
 ...prev,
 music_urls: prev.music_urls.filter((_, i) => i !== index)
 }));
 };

 const addSocialLink = () => {
 if (newSocialPlatform.trim() && newSocialUrl.trim()) {
 setFormData(prev => ({
 ...prev,
 social_links: {
 ...prev.social_links,
 [newSocialPlatform.toLowerCase()]: newSocialUrl.trim()
 }
 }));
 setNewSocialPlatform('');
 setNewSocialUrl('');
 }
 };

 const removeSocialLink = (platform) => {
 setFormData(prev => {
 const newLinks = { ...prev.social_links };
 delete newLinks[platform];
 return { ...prev, social_links: newLinks };
 });
 };

 const handleImageUpload = async (e) => {
 const file = e.target.files?.[0];
 if (!file) return;

 if (!file.type.startsWith('image/')) {
 toast.error('Please select an image file');
 return;
 }

 if (file.size > 5 * 1024 * 1024) {
 toast.error('Image must be less than 5MB');
 return;
 }

 setUploadingImage(true);
 try {
 const formDataUpload = new FormData();
 formDataUpload.append('file', file);

 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/upload/image`, {
 method:'POST',
 headers: token ? {'Authorization': `Bearer ${token}` } : {},
 body: formDataUpload
 });

 if (response.ok) {
 const data = await response.json();
 setFormData(prev => ({ ...prev, image_url: data.url }));
 toast.success('Image uploaded successfully');
 } else {
 toast.error('Failed to upload image');
 }
 } catch (error) {
 toast.error('Error uploading image');
 } finally {
 setUploadingImage(false);
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-slate-50 flex items-center justify-center">
 <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-slate-50 py-8">
 <div className="container mx-auto px-4 max-w-3xl">
 {/* Header */}
 <div className="mb-8">
 <Button
 variant="ghost"
 onClick={() => navigate(isAdminRoute ? '/admin' : '/dashboard')}
 className="mb-4"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 {isAdminRoute ? 'Back to Admin' : 'Back to Dashboard'}
 </Button>
 <div className="flex items-center gap-3">
 <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
 <Music2 className="w-6 h-6 text-violet-600" />
 </div>
 <div>
 <h1 className="text-2xl font-bold text-slate-900">
 {isEditing ?'Edit Artist Profile' :'Create Artist Profile'}
 </h1>
 <p className="text-slate-600">
 {isEditing ?'Update your artist information' :'Register as an artist/performer'}
 </p>
 </div>
 </div>
 </div>

 <form onSubmit={handleSubmit} className="space-y-6">
 {/* Basic Info */}
 <Card>
 <CardHeader>
 <CardTitle>Basic Information</CardTitle>
 <CardDescription>Your name and main details</CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <Label htmlFor="name">Full Name *</Label>
 <Input
 id="name"
 value={formData.name}
 onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
 placeholder="Your full name"
 required
 data-testid="artist-name-input"
 />
 </div>
 <div>
 <Label htmlFor="stage_name">Stage Name</Label>
 <Input
 id="stage_name"
 value={formData.stage_name}
 onChange={(e) => setFormData(prev => ({ ...prev, stage_name: e.target.value }))}
 placeholder="Your stage/performer name (optional)"
 data-testid="artist-stage-name-input"
 />
 </div>
 </div>

 <div>
 <Label htmlFor="location">Location *</Label>
 <Input
 id="location"
 value={formData.location}
 onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
 placeholder="City, County or Region"
 required
 data-testid="artist-location-input"
 />
 </div>

 <div>
 <Label htmlFor="bio">Bio *</Label>
 <Textarea
 id="bio"
 value={formData.bio}
 onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
 placeholder="Tell us about yourself, your performance style, and experience..."
 rows={5}
 required
 data-testid="artist-bio-input"
 />
 </div>

 <div>
 <Label>Profile Image</Label>
 <div className="mt-2 space-y-4">
 {/* Image Preview */}
 {formData.image_url && (
 <div className="relative w-40 h-40 rounded-xl overflow-hidden border border-slate-200">
 <img 
 src={formData.image_url} 
 alt="Profile preview" 
 className="w-full h-full object-cover"
 onError={(e) => { e.target.style.display ='none'; }}
 />
 <button
 type="button"
 onClick={() => setFormData(prev => ({ ...prev, image_url:'' }))}
 className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
 >
 <X className="w-4 h-4" />
 </button>
 </div>
 )}
 
 {/* Upload Button */}
 <div className="flex gap-3">
 <Button
 type="button"
 variant="outline"
 onClick={() => imageInputRef.current?.click()}
 disabled={uploadingImage}
 className="gap-2"
 >
 {uploadingImage ? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ) : (
 <Upload className="w-4 h-4" />
 )}
 {uploadingImage ?'Uploading...' :'Upload Image'}
 </Button>
 <input
 ref={imageInputRef}
 type="file"
 accept="image/*"
 onChange={handleImageUpload}
 className="hidden"
 />
 </div>
 
 {/* Or enter URL manually */}
 <div className="text-sm text-slate-500">Or enter image URL directly:</div>
 <Input
 id="image_url"
 type="url"
 value={formData.image_url}
 onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
 placeholder="https://example.com/your-photo.jpg"
 data-testid="artist-image-input"
 />
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Performance Types */}
 <Card>
 <CardHeader>
 <CardTitle>Performance Type</CardTitle>
 <CardDescription>What type of performer are you?</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="flex flex-wrap gap-2">
 {PERFORMANCE_TYPE_OPTIONS.map(type => (
 <Badge
 key={type}
 variant={formData.performance_types.includes(type) ?'default' :'outline'}
 className={`cursor-pointer transition-colors ${
 formData.performance_types.includes(type)
 ?'bg-violet-600 hover:bg-violet-700'
 :'hover:bg-violet-100'
 }`}
 onClick={() => togglePerformanceType(type)}
 >
 {type}
 </Badge>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Genres */}
 <Card>
 <CardHeader>
 <CardTitle>Genres</CardTitle>
 <CardDescription>What genres do you perform?</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="flex flex-wrap gap-2">
 {GENRE_OPTIONS.map(genre => (
 <Badge
 key={genre}
 variant={formData.genres.includes(genre) ?'default' :'outline'}
 className={`cursor-pointer transition-colors ${
 formData.genres.includes(genre)
 ?'bg-violet-600 hover:bg-violet-700'
 :'hover:bg-violet-100'
 }`}
 onClick={() => toggleGenre(genre)}
 >
 {genre}
 </Badge>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Contact Details */}
 <Card>
 <CardHeader>
 <CardTitle>Contact Details</CardTitle>
 <CardDescription>How can people reach you?</CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <Label htmlFor="email">Email</Label>
 <Input
 id="email"
 type="email"
 value={formData.email}
 onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
 placeholder="your@email.com"
 data-testid="artist-email-input"
 />
 </div>
 <div>
 <Label htmlFor="phone">Phone</Label>
 <Input
 id="phone"
 value={formData.phone}
 onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
 placeholder="Your phone number"
 data-testid="artist-phone-input"
 />
 </div>
 </div>
 <div>
 <Label htmlFor="website">Website</Label>
 <Input
 id="website"
 type="url"
 value={formData.website}
 onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
 placeholder="https://yourwebsite.com"
 data-testid="artist-website-input"
 />
 </div>
 </CardContent>
 </Card>

 {/* Social Links */}
 <Card>
 <CardHeader>
 <CardTitle>Social Media</CardTitle>
 <CardDescription>Add your social media profiles</CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 {/* Existing links */}
 {Object.entries(formData.social_links).map(([platform, url]) => (
 <div key={platform} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
 <span className="capitalize font-medium text-sm min-w-[80px]">{platform}</span>
 <span className="text-sm text-slate-500 truncate flex-1">{url}</span>
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={() => removeSocialLink(platform)}
 >
 <X className="w-4 h-4" />
 </Button>
 </div>
 ))}

 {/* Add new link */}
 <div className="flex gap-2">
 <select
 value={newSocialPlatform}
 onChange={(e) => setNewSocialPlatform(e.target.value)}
 className="px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
 >
 <option value="">Select platform</option>
 <option value="instagram">Instagram</option>
 <option value="facebook">Facebook</option>
 <option value="youtube">YouTube</option>
 <option value="twitter">Twitter/X</option>
 <option value="tiktok">TikTok</option>
 <option value="spotify">Spotify</option>
 </select>
 <Input
 value={newSocialUrl}
 onChange={(e) => setNewSocialUrl(e.target.value)}
 placeholder="Profile URL"
 className="flex-1"
 />
 <Button type="button" variant="outline" onClick={addSocialLink}>
 <Plus className="w-4 h-4" />
 </Button>
 </div>
 </CardContent>
 </Card>

 {/* Video Links */}
 <Card>
 <CardHeader>
 <CardTitle>Performance Videos</CardTitle>
 <CardDescription>Links to YouTube, Vimeo, or other video platforms</CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 {formData.video_urls.map((url, index) => (
 <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
 <span className="text-sm text-slate-600 truncate flex-1">{url}</span>
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={() => removeVideoUrl(index)}
 >
 <X className="w-4 h-4" />
 </Button>
 </div>
 ))}
 <div className="flex gap-2">
 <Input
 value={newVideoUrl}
 onChange={(e) => setNewVideoUrl(e.target.value)}
 placeholder="https://youtube.com/watch?v=..."
 className="flex-1"
 />
 <Button type="button" variant="outline" onClick={addVideoUrl}>
 <Plus className="w-4 h-4 mr-2" />
 Add
 </Button>
 </div>
 </CardContent>
 </Card>

 {/* Music Links */}
 <Card>
 <CardHeader>
 <CardTitle>Music Samples</CardTitle>
 <CardDescription>Links to Spotify, SoundCloud, or music samples</CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 {formData.music_urls.map((url, index) => (
 <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
 <span className="text-sm text-slate-600 truncate flex-1">{url}</span>
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={() => removeMusicUrl(index)}
 >
 <X className="w-4 h-4" />
 </Button>
 </div>
 ))}
 <div className="flex gap-2">
 <Input
 value={newMusicUrl}
 onChange={(e) => setNewMusicUrl(e.target.value)}
 placeholder="https://spotify.com/track/..."
 className="flex-1"
 />
 <Button type="button" variant="outline" onClick={addMusicUrl}>
 <Plus className="w-4 h-4 mr-2" />
 Add
 </Button>
 </div>
 </CardContent>
 </Card>

 {/* Submit */}
 <div className="flex gap-4">
 <Button
 type="button"
 variant="outline"
 onClick={() => navigate(isAdminRoute ? '/admin' : '/dashboard')}
 className="flex-1"
 >
 Cancel
 </Button>
 <Button
 type="submit"
 disabled={saving}
 className="flex-1 bg-violet-600 hover:bg-violet-700"
 data-testid="artist-submit-btn"
 >
 {saving ? (
 <>
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 Saving...
 </>
 ) : (
 <>
 <Save className="w-4 h-4 mr-2" />
 {isEditing ?'Update Profile' :'Create Profile'}
 </>
 )}
 </Button>
 </div>

 {!isEditing && (
 <p className="text-sm text-slate-500 text-center">
 Note: Your profile will be reviewed before appearing in the directory.
 </p>
 )}
 </form>
 </div>
 </div>
 );
}
